import React from 'react';
import { get, cloneDeep, set } from 'lodash';

// local dependencies
import { memberCheckoutAssigningCoverage as MemberCheckoutAssigningCoverageView } from 'ui/views/widgets/memberCheckoutAssigningCoverage';
import { MemberCheckoutAssigningCoverageService } from 'services/memberCheckoutAssigningCoverage';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberCheckoutAssigningCoverage } from 'ui/containers/MemberCheckoutAssigningCoverage';
 *
 * // render
 * <MemberCheckoutAssigningCoverage propName='propValue'/>
 */
export class MemberCheckoutAssigningCoverage extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind function
        this.handleCoverageSelection = this.handleCoverageSelection.bind( this );

        // initial state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            selectedCoverageValue: null
        };

        // create new service instance
        this.memberCheckoutAssigningCoverageService = new MemberCheckoutAssigningCoverageService();

        // start component data fetch
        this.fetchComponentData();
    }

    /**
     * Handles fetching of component data
     */
    async fetchComponentData() {
        log.debug( 'MemberCheckoutAssigningCoverage.fetchComponentData()' );

        // cancel ongoing service request
        this.memberCheckoutAssigningCoverageService.cancel();

        // fetch component data
        const { status, data } = await this.memberCheckoutAssigningCoverageService.fetchComponentData( {
            url: get( this.props, 'widget.url' ),
            data: {
                context: get( this.props, 'requestPayload.context', {} )
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            // get the value of coverage from received data
            const defaultCoverageValue = get( data, 'options.assignCoverageToRefillsOptions[0].value' );

            // get readability value of coverage field
            const readonly = get( data, 'values.readonly', false );

            // update state with received data
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: {
                    fields: get( data, 'fields' ),
                    options: get( data, 'options' ),
                    values: { readonly }
                }
            }, () => {

                // set default value for coverage
                this.handleCoverageSelection( { value: defaultCoverageValue } );
            } );
        }
    }

    /**
     * Handles coverage selection
     */
    handleCoverageSelection( { value } ) {
        log.debug( 'MemberCheckoutAssigningCoverage.handleCoverageSelection()' );

        // deep clone component data state
        const componentData = cloneDeep( this.state.componentData );

        // set `value` of coverage in component data state
        set( componentData, 'values.assignCoverageToRefills', value );
        const assignCoverageToRefillsOptions = get( componentData, 'options.assignCoverageToRefillsOptions' );
        if (assignCoverageToRefillsOptions && assignCoverageToRefillsOptions.length) {
            const selectedOption = assignCoverageToRefillsOptions.find(o => value === o?.value);
            this.props.updateEnableNextForDavita( true );
        }

        // update state and checkout selection
        this.setState( { componentData }, () => {
            this.props.updateCheckoutSelection( { type: 'coverage', item: get( this.state.componentData, 'values.assignCoverageToRefills' )} );
        } );
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberCheckoutAssigningCoverageView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...this.state.componentData }
                title={ get( this.props, 'widget.title' )}
                onChange={ this.handleCoverageSelection }
            />
        );
    }
}

// set display name
MemberCheckoutAssigningCoverage.displayName = 'MemberCheckoutAssigningCoverage';

// set default props
MemberCheckoutAssigningCoverage.defaultProps = {};

// prop types
MemberCheckoutAssigningCoverage.propTypes = {};
